import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ScoreMeter from "../components/ScoreMeter";
import VideoPoster from "../components/VideoPoster";
import VibeTag from "../components/VibeTag";
import { getToken } from "../lib/auth";
import { getNextJourneyPath } from "../config/journeyConfig";
import { track } from "../lib/analytics";

export default function FinalistComparison() {
  const navigate = useNavigate();
  const nextPath = getNextJourneyPath("/finalists") ?? "/reveal";
  const [finalistProspects, setFinalistProspects] = useState([]);
  const [pick, setPick] = useState(null);

  useEffect(() => { track('finalists_viewed'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the user's shortlist and derive top-2 by compatibility score
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    Promise.all([
      fetch('/api/prospects', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null)),
      fetch('/api/shortlist', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([allProspects, sl]) => {
        if (!allProspects?.length) return;
        let pool = allProspects; // already sorted by compatibility_score desc
        if (sl?.prospect_ids?.length) {
          const slSet = new Set(sl.prospect_ids);
          const inShortlist = allProspects.filter((p) => slSet.has(p.id));
          if (inShortlist.length >= 2) pool = inShortlist;
        }
        setFinalistProspects([pool[0], pool[1]]);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (finalistProspects.length < 2) {
    return (
      <AppShell>
        <div className="px-6 py-20 text-center">
          <div className="glass rounded-3xl p-10 max-w-sm mx-auto">
            <div className="font-display text-2xl mb-3">Finalists not ready yet</div>
            <p className="text-white/50 text-sm">Complete your rounds to reach the finalist stage.</p>
            <Link to="/bracket" className="mt-6 inline-flex rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 text-sm">
              Continue journey
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  const [a, b] = finalistProspects;

  const cats = [
    ["Values alignment", a.scores.values, b.scores.values, "gold"],
    ["Communication", a.scores.communication, b.scores.communication, "gold"],
    ["Chemistry", a.scores.chemistry, b.scores.chemistry, "burgundy"],
    ["Lifestyle fit", a.scores.lifestyle, b.scores.lifestyle, "forest"],
    ["Future goals", a.scores.futureGoals, b.scores.futureGoals, "forest"],
    ["Effort", a.scores.effort, b.scores.effort, "gold"],
  ];

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
          Round 06 · Finalists
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.02] mb-3">
          Two remain.
        </h1>
        <p className="text-white/60 max-w-2xl mb-6">
          No scores. No speed. Just you — and the two people you've chosen to
          know most. Take your time.
        </p>

        {/* Decision moment prompt */}
        <div
          data-testid="decision-prompt"
          className="glass rounded-2xl px-5 py-4 mb-10 flex items-center gap-4 max-w-2xl"
        >
          <span className="h-9 w-9 rounded-full grid place-items-center bg-[rgba(212,175,55,0.1)] ring-1 ring-[rgba(212,175,55,0.3)]">
            <svg viewBox="0 0 24 24" className="h-4 w-4 stroke-[#F2DE94]" fill="none" strokeWidth="1.6">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v6l3 2" />
            </svg>
          </span>
          <div className="flex-1">
            <div className="font-display text-lg leading-tight">
              Take a moment. This choice matters.
            </div>
            <div className="text-xs text-white/50 mt-0.5">
              You can return to this screen at any time before confirming.
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {[a, b].map((p, idx) => {
            const picked = pick === (idx === 0 ? "A" : "B");
            const other = pick && !picked;
            return (
              <div
                key={p.id}
                data-testid={`finalist-${idx === 0 ? "a" : "b"}`}
                className={`relative rounded-3xl overflow-hidden transition-all duration-[400ms] ease-out ${
                  picked
                    ? "ring-gold glow-gold scale-[1.01]"
                    : other
                      ? "ring-1 ring-white/8 opacity-45 scale-[0.97] blur-[0.5px] saturate-[0.6]"
                      : "ring-1 ring-white/8"
                }`}
              >
                <div className="relative h-[460px]">
                  <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/40 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#F2DE94]">
                      Finalist {idx === 0 ? "A" : "B"}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <VideoPoster
                      src={p.videoPoster || p.image}
                      label="Final intro"
                      duration="0:22"
                      className="h-40 w-28"
                    />
                  </div>
                  <div className="absolute bottom-5 left-6 right-6">
                    <div className="font-display text-4xl leading-none">{p.name}</div>
                    <div className="text-sm text-white/65 mt-1">{p.occupation} · {p.city}</div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.tags.map((t) => (
                        <VibeTag key={t} tone="ghost">{t}</VibeTag>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-[#0C111D] p-6 md:p-7 border-t border-white/5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-2">
                    Why they're here
                  </div>
                  <p className="font-display italic text-lg text-white/85 leading-snug">
                    "{p.prompts[0]?.a}"
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                    <div className="rounded-xl bg-[rgba(74,163,106,0.08)] border border-[rgba(74,163,106,0.25)] p-3">
                      <div className="uppercase tracking-[0.2em] text-[#8FD6A6]">Strengths</div>
                      <div className="mt-1 text-white/80">{p.greenFlags.slice(0, 2).join(" · ")}</div>
                    </div>
                    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                      <div className="uppercase tracking-[0.2em] text-white/55">Notes</div>
                      <div className="mt-1 text-white/70">{p.cautions[0]}</div>
                    </div>
                  </div>

                  <button
                    onClick={() => setPick(idx === 0 ? "A" : "B")}
                    data-testid={`pick-finalist-${idx === 0 ? "a" : "b"}`}
                    className={`mt-6 w-full rounded-full font-semibold py-3.5 transition-all ${
                      picked
                        ? "bg-gradient-gold text-[#05070d] glow-gold"
                        : "bg-white/5 hover:bg-white/10 text-white/85 border border-white/10"
                    }`}
                  >
                    {picked ? "✓ Strongest connection" : "Choose as strongest connection"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison */}
        <div className="glass-strong rounded-3xl p-6 md:p-8">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37]">
                Side by side
              </div>
              <div className="font-display text-2xl mt-1">How they compare</div>
            </div>
            <div className="text-xs text-white/45">
              AI recommendation:{" "}
              <span className="text-[#F2DE94]">Finalist A · by 4 points</span>
            </div>
          </div>

          <div className="space-y-4">
            {cats.map(([k, av, bv, tone]) => (
              <div key={k} className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                <div>
                  <ScoreMeter label={a.name} value={av} tone={tone} compact />
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 w-24 text-center">
                  {k}
                </div>
                <div className="scale-x-[-1]">
                  <ScoreMeter label={b.name} value={bv} tone={tone} compact />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex justify-end">
          <button
            onClick={async () => {
              const token = getToken();
              if (token) {
                try {
                  await fetch('/api/finalists', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ finalist_ids: [a.id, b.id] }),
                  });
                } catch {}
              }
              navigate(nextPath);
            }}
            data-testid="reveal-winner" data-flow="advance-step"
            className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-7 py-3.5 glow-gold hover:scale-[1.02] transition-transform"
          >
            Confirm & reveal →
          </button>
        </div>
      </div>
    </AppShell>
  );
}
